import { useState } from 'react';
import { useBilling, useBillingCancellation } from '../../../platform/api/hooks';
import { ApiError } from '../../../platform/api/client';
import { PageHeader } from '../../../components/ui/PageHeader';
import { Section } from '../../../components/ui/Section';
import { Badge } from '../../../components/ui/Badge';
import { EmptyState } from '../../../components/ui/EmptyState';
import { CheckoutForm } from './billing-checkout';
import {
  ACCESS_LABELS,
  STATUS_LABELS,
  type BillingOverview,
  fmtDate,
  fmtMoney,
  newIdempotencyKey,
} from './billing.support';

export function BillingPage() {
  const { data, isLoading, error } = useBilling();
  const cancel = useBillingCancellation();
  const [selectedPlan, setSelectedPlan] = useState<string | null>(null);

  const wrap = (children: React.ReactNode) => (
    <div style={{ padding: '2.25rem 2.5rem', maxWidth: 1100 }}>
      <PageHeader title="Assinatura" subtitle="Plano, pagamentos e situação da casa" />
      {children}
    </div>
  );

  if (isLoading) return wrap(<div className="skeleton" style={{ height: 180 }} />);
  if (error) {
    const unavailable = error instanceof ApiError && error.status === 404;
    return wrap(
      <EmptyState
        title={unavailable ? 'A assinatura ainda não está disponível.' : 'Não foi possível abrir.'}
        copy={
          unavailable
            ? 'A contratação pelo aplicativo será liberada em breve. Fale com a equipe do Zeladoria.'
            : error.message
        }
      />,
    );
  }

  const view = data as BillingOverview;
  const current = view.subscription;
  const hasSubscription = Boolean(current?.subscription);

  return wrap(
    <div style={{ display: 'grid', gap: '1.5rem' }}>
      {view.unavailable.length > 0 && (
        <p className="form-error">
          Parte das informações de cobrança não respondeu agora. Tente novamente em instantes.
        </p>
      )}

      {current && (
        <Section
          title="Situação da casa."
          action={
            <Badge
              label={ACCESS_LABELS[current.access.level]?.label ?? current.access.level}
              variant={ACCESS_LABELS[current.access.level]?.variant ?? 'default'}
            />
          }
        >
          {current.subscription && current.plan ? (
            <div style={{ display: 'grid', gap: '0.4rem', fontSize: '0.85rem' }}>
              <strong style={{ fontSize: '1.05rem' }}>{current.plan.name}</strong>
              <span>
                {fmtMoney(current.plan.amountMinor, current.plan.currency)} por{' '}
                {current.plan.billingInterval === 'year' ? 'ano' : 'mês'} ·{' '}
                {STATUS_LABELS[current.subscription.status] ?? current.subscription.status}
              </span>
              <span style={{ color: 'var(--muted)' }}>
                {current.subscription.cancelAtPeriodEnd
                  ? `Encerra em ${fmtDate(current.subscription.currentPeriodEnd)}.`
                  : `Período atual até ${fmtDate(current.subscription.currentPeriodEnd)}.`}
              </span>
              {current.openInvoice && (
                <span style={{ color: 'var(--terra)' }}>
                  Fatura em aberto de{' '}
                  {fmtMoney(current.openInvoice.totalMinor, current.openInvoice.currency)} com
                  vencimento em {fmtDate(current.openInvoice.dueAt)}.
                </span>
              )}
              {!current.subscription.cancelAtPeriodEnd && (
                <div style={{ marginTop: '0.6rem' }}>
                  <button
                    type="button"
                    className="btn btn-outline"
                    disabled={cancel.isPending}
                    onClick={() => {
                      if (window.confirm('Cancelar a assinatura ao fim do período atual?')) {
                        cancel.mutate(newIdempotencyKey());
                      }
                    }}
                  >
                    {cancel.isPending ? 'Cancelando…' : 'Cancelar ao fim do período'}
                  </button>
                  {cancel.error && <p className="form-error">{cancel.error.message}</p>}
                </div>
              )}
            </div>
          ) : (
            <p style={{ fontSize: '0.85rem', color: 'var(--muted)' }}>
              A casa ainda não tem assinatura ativa.
            </p>
          )}
        </Section>
      )}

      {!hasSubscription && (
        <Section title="Planos disponíveis.">
          {view.plans.length === 0 ? (
            <EmptyState
              title="Nenhum plano publicado agora."
              copy="Fale com a equipe do Zeladoria para contratar."
            />
          ) : (
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
                gap: '1rem',
              }}
            >
              {view.plans.map((plan) => (
                <button
                  key={plan.code}
                  type="button"
                  className={`metric-card clickable${selectedPlan === plan.code ? ' highlight' : ''}`}
                  onClick={() => {
                    setSelectedPlan(plan.code);
                  }}
                >
                  <span className="metric-label">{plan.name}</span>
                  <span className="metric-value">{fmtMoney(plan.amountMinor, plan.currency)}</span>
                  <span className="metric-detail">
                    por {plan.billingInterval === 'year' ? 'ano' : 'mês'}
                  </span>
                </button>
              ))}
            </div>
          )}
          {selectedPlan && <CheckoutForm key={selectedPlan} planCode={selectedPlan} />}
        </Section>
      )}

      <Section title="Faturas.">
        {view.invoices.length === 0 ? (
          <EmptyState title="Nenhuma fatura por aqui." />
        ) : (
          <table>
            <thead>
              <tr>
                <th>Vencimento</th>
                <th>Valor</th>
                <th>Situação</th>
              </tr>
            </thead>
            <tbody>
              {view.invoices.map((invoice) => (
                <tr key={invoice.id}>
                  <td>{fmtDate(invoice.dueAt)}</td>
                  <td>{fmtMoney(invoice.totalMinor, invoice.currency)}</td>
                  <td>{invoice.paidAt ? `Paga em ${fmtDate(invoice.paidAt)}` : invoice.status}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Section>
    </div>,
  );
}
